import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  clearAccessToken,
  configureAuthRecovery,
  setAccessToken,
} from "../../../shared/auth/authSession";
import { authApi } from "../api/authApi";
import type { AuthUser, LoginRequest } from "./types";

type AuthStatus = "loading" | "anonymous" | "authenticated";

type AuthContextValue = {
  status: AuthStatus;
  user: AuthUser | null;
  login: (payload: LoginRequest) => Promise<AuthUser>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

let restorePromise: Promise<AuthUser> | null = null;

const restoreAuthentication = (): Promise<AuthUser> => {
  if (!restorePromise) {
    restorePromise = (async () => {
      const token = await authApi.refresh();
      setAccessToken(token.access_token);
      return authApi.getMe();
    })().finally(() => {
      restorePromise = null;
    });
  }
  return restorePromise;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  const becomeAnonymous = useCallback(() => {
    clearAccessToken();
    localStorage.removeItem("has_session");
    setUser(null);
    setStatus("anonymous");
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string> => {
    const token = await authApi.refresh();
    setAccessToken(token.access_token);
    return token.access_token;
  }, []);

  useEffect(
    () =>
      configureAuthRecovery({
        refreshAccessToken,
        onUnauthorized: becomeAnonymous,
      }),
    [becomeAnonymous, refreshAccessToken],
  );

  useEffect(() => {
    let active = true;
    const hasSession = localStorage.getItem("has_session");
    if (!hasSession) {
      setStatus("anonymous");
      return;
    }
    restoreAuthentication()
      .then((currentUser) => {
        if (!active) return;
        setUser(currentUser);
        setStatus("authenticated");
      })
      .catch(() => {
        if (active) becomeAnonymous();
      });
    return () => {
      active = false;
    };
  }, [becomeAnonymous]);

  const login = useCallback(
    async (payload: LoginRequest): Promise<AuthUser> => {
      const token = await authApi.login(payload);
      setAccessToken(token.access_token);
      try {
        const currentUser = await authApi.getMe();
        setUser(currentUser);
        setStatus("authenticated");
        localStorage.setItem("has_session", "true");
        return currentUser;
      } catch (error) {
        clearAccessToken();
        throw error;
      }
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // Clear browser memory even when the shared backend is unavailable.
    } finally {
      becomeAnonymous();
    }
  }, [becomeAnonymous]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout }),
    [status, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};
